import { eventChannel } from 'redux-saga';
import qs from 'query-string';
import { all, takeLatest, take, fork, put, putResolve, select } from 'redux-saga/effects';
import { anyPass, eqProps, complement, pick } from 'ramda';
import {
  AuthActions,
  AuthTypes,
  registerRoutine,
  loginRoutine,
  resetPasswordRoutine,
  changePasswordRoutine,
  updateUserDataRoutine,
} from './auth.redux';
import { selectAuthModeParams, selectUserData, selectUserId, selectAuthAttempts } from './auth.selectors';
import { auth, firebaseFunctions, usersRef } from '../../shared/services/firebase';
import browserHistory from '../../shared/utils/history';
import {
  AUTH_MODES,
  AUTH_MODES_ROUTES,
  getFirebaseAuthErrorMessage,
  PROFILE_FIELDS,
  RESET_PASSWORD_FIELDS,
  RESET_PASSWORD_TAB_SUCCESS,
} from './auth.constants';
import { SnackbarActions } from '../snackbar';
import { AnalyticsActions, GTMEvents } from '../analytics';
import {
  SNACKBAR_ERROR,
  SNACKBAR_LOGOUT_SUCCESS,
  SNACKBAR_YOUR_PROFILE_UPDATED,
  SNACKBAR_RESET_PASSWORD_EMAIL_SENT,
} from '../snackbar/snackbar.constants';
import messages from './auth.messages';
import { ROUTES } from '../../routes/routes';
import { getLocationPathname, getLocationQuery } from '../../shared/utils/location';
import { selectUserData as selectCurrentUser, UserActions } from '../user';
import { EDITABLE_FIELDS } from '../../routes/account/settings/yourProfileForm/yourProfileForm.constants';
import { ItineraryActions } from '../itinerary';

const createAuthChannel = () =>
  eventChannel(emitter => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setTimeout(() => {
        if (user) {
          emitter({
            user,
            authenticated: true,
          });
        } else {
          emitter({
            authenticated: false,
          });
        }
      });
    });

    return () => unsubscribe();
  });

function* startListeningForAuth() {
  const listenForAuthChannel = yield createAuthChannel();

  while (true) {
    yield put(UserActions.setIsBeingListened(true));
    const { authenticated, user } = yield take(listenForAuthChannel);

    if (!authenticated) {
      yield put(AuthActions.userNotAuthenticated());
    } else {
      const currentUserData = yield select(selectUserData);
      const hasUserChanged = anyPass([complement(eqProps)('uid'), complement(eqProps)('isAnonymous')])(
        currentUserData,
        user.toJSON()
      );

      if (hasUserChanged) {
        const idToken = yield user.getIdToken();
        yield put(ItineraryActions.clear());
        yield put(AuthActions.userAuthenticated(user.toJSON(), idToken));
        yield put(UserActions.listenForUserData());
      }
    }
    yield put(UserActions.setIsBeingListened(false));
  }
}

function* listenForAuthSaga() {
  yield fork(startListeningForAuth);
}

function* signInAnonymously() {
  yield auth.signInAnonymouslyAndRetrieveData();
}

function* signOut() {
  yield putResolve(AuthActions.willReAuth());
  yield auth.signOut();
  if (!getLocationPathname().includes(ROUTES.kioskBase)) {
    browserHistory.push(ROUTES.home);
  }

  yield put(SnackbarActions.showSnackbar(SNACKBAR_LOGOUT_SUCCESS));
}

function* userNotAuthenticated() {
  yield put(UserActions.clear());
  yield put(AuthActions.signInAnonymously());
  firebaseFunctions.defaults.headers.common.Authorization = undefined;
}

function* userAuthenticated({ idToken }) {
  firebaseFunctions.defaults.headers.common.Authorization = `Bearer ${idToken}`;
}

function* updateUserDataTrigger({ payload }) {
  yield put(updateUserDataRoutine.request(payload));
}

function* updateUserDataRequest({ payload }) {
  yield put(UserActions.setIsUpdating(true));
  const profile = pick(EDITABLE_FIELDS)(payload);
  const uid = yield select(selectUserId);
  const currentUserData = yield select(selectUserData);

  try {
    if (profile.email !== currentUserData.email) {
      yield auth.currentUser.updateEmail(profile.email);
    }

    yield usersRef.child(uid).update(profile);

    yield put(updateUserDataRoutine.success());
    yield put(SnackbarActions.showSnackbar(SNACKBAR_YOUR_PROFILE_UPDATED));
  } catch (error) {
    yield put(updateUserDataRoutine.failure({ errors: getFirebaseAuthErrorMessage(error) }));
    yield put(SnackbarActions.showSnackbar(SNACKBAR_ERROR));
  } finally {
    yield put(updateUserDataRoutine.fulfill());
    yield put(UserActions.setIsUpdating(false));
  }
}

function* logGTMEventWithAuthAttempt(event, shouldClearAttempts) {
  yield put(AuthActions.incrementAuthAttempts());
  const attempts = yield select(selectAuthAttempts);

  yield put(AnalyticsActions.logGTMEvent(event, { attempts }));

  if (shouldClearAttempts) {
    yield put(AuthActions.clearAuthAttempts());
  }
}

function* registerTrigger({ payload }) {
  yield put(registerRoutine.request(payload));
}

function* registerRequest({ payload }) {
  const { email, password, ...userData } = payload;

  try {
    const { user } = yield auth.createUserWithEmailAndPassword(email, password);
    yield take(AuthTypes.USER_AUTHENTICATED);
    yield put(registerRoutine.success({ ...user, ...userData }));
  } catch (error) {
    yield put(registerRoutine.failure({ errors: getFirebaseAuthErrorMessage(error) }));
  } finally {
    yield put(registerRoutine.fulfill());
  }
}

function* registerSuccess({ payload }) {
  yield put(AuthActions.hideModal());

  const profile = pick(PROFILE_FIELDS)(payload);
  yield usersRef.child(payload.uid).set({
    ...profile,
    isWebUser: true,
  });

  yield logGTMEventWithAuthAttempt(GTMEvents.AuthSignupSuccess, true);
  yield put(AnalyticsActions.logGTMEvent(GTMEvents.AuthComplete));
}

function* registerFailure() {
  yield logGTMEventWithAuthAttempt(GTMEvents.AuthSignupFail);
}

function* loginTrigger({ payload }) {
  yield put(loginRoutine.request(payload));
}

function* loginRequest({ payload }) {
  const { email, password } = payload;

  try {
    yield auth.signInWithEmailAndPassword(email, password);
    yield put(loginRoutine.success());
  } catch (error) {
    yield put(loginRoutine.failure({ errors: getFirebaseAuthErrorMessage(error) }));
  } finally {
    yield put(loginRoutine.fulfill());
  }
}

function* loginSuccess() {
  yield put(AuthActions.hideModal());
  yield logGTMEventWithAuthAttempt(GTMEvents.AuthLoginSuccess, true);
  yield put(AnalyticsActions.logGTMEvent(GTMEvents.AuthComplete));
}

function* loginFailure() {
  yield logGTMEventWithAuthAttempt(GTMEvents.AuthLoginFail);
}

function* changePassword() {
  const currentUser = yield select(selectCurrentUser);
  yield put(UserActions.setIsChangingPassword(true));
  yield put(resetPasswordRoutine.trigger(currentUser));
}

function* resetPasswordTrigger({ payload }) {
  yield put(resetPasswordRoutine.request(payload));
}

function* resetPasswordRequest({ payload }) {
  const { email } = payload;

  try {
    yield firebaseFunctions.post('/webPasswordReset/', { email });
    yield put(resetPasswordRoutine.success());
    if (getLocationPathname().includes(ROUTES.account.base)) {
      yield put(SnackbarActions.showSnackbar(SNACKBAR_RESET_PASSWORD_EMAIL_SENT));
    }
  } catch (error) {
    yield put(resetPasswordRoutine.failure({ errors: { [RESET_PASSWORD_FIELDS.email]: messages.unknownError } }));
    yield put(SnackbarActions.showSnackbar(SNACKBAR_ERROR));
  } finally {
    yield put(resetPasswordRoutine.fulfill());
    yield put(UserActions.setIsChangingPassword(false));
  }
}

function* resetPasswordSuccess() {
  yield put(AuthActions.hideModal());
  if (!getLocationPathname().includes(ROUTES.account.base)) {
    yield put(AuthActions.showModal(RESET_PASSWORD_TAB_SUCCESS));
  }
}

function* changePasswordTrigger({ payload }) {
  yield put(changePasswordRoutine.request(payload));
}

function* changePasswordRequest({ payload }) {
  const { password } = payload;
  const { oobCode: code, email } = yield select(selectAuthModeParams);

  try {
    yield auth.confirmPasswordReset(code, password);
    yield auth.signInWithEmailAndPassword(email, password);
    yield put(changePasswordRoutine.success());
  } catch (error) {
    yield put(changePasswordRoutine.failure({ errors: getFirebaseAuthErrorMessage(error) }));
  } finally {
    yield put(changePasswordRoutine.fulfill());
  }
}

function* changePasswordSuccess() {
  browserHistory.push(ROUTES.auth.changePassword.success);
}

function* initAuthMode() {
  const { mode, ...params } = qs.parse(getLocationQuery()) || {};

  const isAuthMode = AUTH_MODES.includes(mode);

  if (!isAuthMode) {
    browserHistory.push(ROUTES.home);
    return;
  }

  yield put(AuthActions.initAuthModeSuccess(mode, params));
}

function* initAuthModeSuccess({ mode }) {
  browserHistory.push(AUTH_MODES_ROUTES[mode]);
}

export default function* startupSaga() {
  yield all([
    takeLatest(AuthTypes.LISTEN_FOR_AUTH, listenForAuthSaga),
    takeLatest(AuthTypes.USER_NOT_AUTHENTICATED, userNotAuthenticated),
    takeLatest(AuthTypes.USER_AUTHENTICATED, userAuthenticated),
    takeLatest(AuthTypes.SIGN_IN_ANONYMOUSLY, signInAnonymously),
    takeLatest(AuthTypes.SIGN_OUT, signOut),
    takeLatest(AuthTypes.INIT_AUTH_MODE, initAuthMode),
    takeLatest(AuthTypes.INIT_AUTH_MODE_SUCCESS, initAuthModeSuccess),
    takeLatest(updateUserDataRoutine.TRIGGER, updateUserDataTrigger),
    takeLatest(updateUserDataRoutine.REQUEST, updateUserDataRequest),
    takeLatest(registerRoutine.TRIGGER, registerTrigger),
    takeLatest(registerRoutine.REQUEST, registerRequest),
    takeLatest(registerRoutine.SUCCESS, registerSuccess),
    takeLatest(registerRoutine.FAILURE, registerFailure),
    takeLatest(loginRoutine.TRIGGER, loginTrigger),
    takeLatest(loginRoutine.REQUEST, loginRequest),
    takeLatest(loginRoutine.SUCCESS, loginSuccess),
    takeLatest(loginRoutine.FAILURE, loginFailure),
    takeLatest(AuthTypes.CHANGE_PASSWORD, changePassword),
    takeLatest(resetPasswordRoutine.TRIGGER, resetPasswordTrigger),
    takeLatest(resetPasswordRoutine.REQUEST, resetPasswordRequest),
    takeLatest(resetPasswordRoutine.SUCCESS, resetPasswordSuccess),
    takeLatest(changePasswordRoutine.TRIGGER, changePasswordTrigger),
    takeLatest(changePasswordRoutine.REQUEST, changePasswordRequest),
    takeLatest(changePasswordRoutine.SUCCESS, changePasswordSuccess),
  ]);
}
